import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { cryptocurrencyExchanges } from "../../../../../../components/ui/forms/selectOptionData";
import { getExchangeCode } from "../../../../../../utils/crypto/cryptoHelpers";
import {
  createAndGetDocument,
  createCryptoOriginDocument,
  updateDocumentWithFile,
} from "../../../../../../api/filings/documentsAPI";
import { Handler401 } from "../../../../../../utils/auth/authHandler";
import { partialUpdateFiling } from "../../../../../../api/filings/filingAPI";

export function useDocumentActions(
  filingUuid,
  filing,
  setFiling,
  fetchDocuments,
  fetchData,
) {
  const navigate = useNavigate();

  // Helper preserved exactly from your source
  const getDocumentType = (sourceName, sourceData = null) => {
    if (sourceName === "rentalProperties" && sourceData?.documentType)
      return sourceData.documentType;
    if (sourceName === "esLoans" && sourceData?.documentType)
      return sourceData.documentType;
    if (sourceName === "rcDistributions" && sourceData?.documentType)
      return sourceData.documentType;
    if (sourceName === "propertiesAssets" && sourceData?.documentType)
      return sourceData.documentType;

    if (sourceName === "cryptoTransaction" && sourceData?.exchange) {
      const exchangeValue = sourceData.exchange.toLowerCase();
      const exchangeConfig = cryptocurrencyExchanges().find(
        (opt) => opt.value === exchangeValue,
      );
      return exchangeConfig?.documentType === "1099" ? "CRYPTO_1099" : "CRYPTO";
    }

    const documentTypeMap = {
      wtwoForm: "W2",
      tenForm: "1099",
      homeOwnership: "1098_HOME",
      rentalProperties: "1098",
      stockOrDividends: "1099_BROKERAGE",
      scheduleK: "SCHEDULE_K",
      cryptoTransaction: "CRYPTO",
      llc_owner: "LLC",
      foreignIncome: "FOREIGN_INCOME_STATEMENT",
      naturalDisaster: "DISASTER_REPORT",
      healthInsuranceMarketplace: "FORM_1095_A",
      healthInsuranceExemption: "HEALTH_COVERAGE_EXEMPTION",
      purchaseAssets: "PURCHASE_ASSETS",
      stateAnnualReport: "STATE_ANNUAL_REPORT",
      gainLoss: "GAIN_LOSS",
      EFILE_SIGNATURE_AUTHORIZATION: "EFILE_SIGNATURE_AUTHORIZATION",
      EFILE_FEDERAL: "EFILE_FEDERAL",
      EFILE_STATE: "EFILE_STATE",
      PROOF_OF_ACCEPTANCE: "PROOF_OF_ACCEPTANCE",
    };
    return documentTypeMap[sourceName] || sourceName.toUpperCase();
  };

  const handleUploadDocument = async (
    file,
    selectedSource,
    selectedDocumentId = null,
    selectedYear = null,
  ) => {
    if (!selectedSource || !filingUuid)
      throw new Error("Missing source or filing ID");
    if (!file) throw new Error("No file selected");

    // Crypto Transaction Logic
    if (
      selectedSource.name === "cryptoTransaction" &&
      selectedSource.documentType === "transactions" &&
      selectedSource.exchange === "kraken"
    ) {
      if (!selectedYear)
        throw new Error("Tax year is required for crypto transactions");
      const exchangeCode = getExchangeCode(selectedSource.exchange);
      if (!exchangeCode)
        throw new Error(`Invalid exchange: ${selectedSource.exchange}`);

      const response = await createCryptoOriginDocument(
        file,
        exchangeCode,
        selectedYear,
        {
          service_id: filingUuid,
          service_model: "taxfiling",
        },
      );

      if (response.status === 401) {
        Handler401(navigate);
        return;
      }
      if (response.status !== 201)
        throw new Error("Failed to upload crypto document");
      return await response.json();
    }

    // Standard Document Logic
    const documentType = getDocumentType(selectedSource.name, selectedSource);
    const isExpertDoc = [
      "EFILE_FEDERAL",
      "EFILE_STATE",
      "PROOF_OF_ACCEPTANCE",
      "EFILE_SIGNATURE_AUTHORIZATION",
    ].includes(documentType);

    let documentYear;
    let serviceType = null;

    if (isExpertDoc) {
      const ptfYears =
        filing.tax_situation?.details?.PTF?.situation?.taxYears || [];
      const btfYears =
        filing.tax_situation?.details?.BTF?.situation?.taxYears || [];
      const ptfArray = Array.isArray(ptfYears) ? ptfYears : [ptfYears];
      const btfArray = Array.isArray(btfYears) ? btfYears : [btfYears];

      if (selectedSource.service) {
        serviceType = selectedSource.service;
        documentYear =
          (serviceType === "PTF" ? ptfArray[0] : btfArray[0]) ||
          new Date().getFullYear();
      } else {
        serviceType =
          ptfArray.length > 0 ? "PTF" : btfArray.length > 0 ? "BTF" : null;
        documentYear =
          (serviceType === "PTF" ? ptfArray[0] : btfArray[0]) ||
          new Date().getFullYear();
      }
    } else {
      const taxYears =
        filing.tax_situation?.details?.PTF?.situation?.taxYears ||
        filing.tax_situation?.details?.BTF?.situation?.taxYears;
      documentYear = Array.isArray(taxYears)
        ? taxYears[0]
        : taxYears || new Date().getFullYear();
    }

    const formData = new FormData();
    formData.append("documentContent", file);
    formData.append("documentName", file.name || "document");
    formData.append("documentType", documentType);
    formData.append("documentYear", String(documentYear));
    formData.append("updateType", "full");
    if (isExpertDoc && serviceType) formData.append("service", serviceType);
    if (selectedSource.propertyIndex != null)
      formData.append("propertyIndex", String(selectedSource.propertyIndex));

    const apiUrl = selectedDocumentId ? "tax/document" : "tax/documents";
    const response = selectedDocumentId
      ? await updateDocumentWithFile(apiUrl, selectedDocumentId, formData, {
          includeMetrics: true,
        })
      : await createAndGetDocument(apiUrl, filingUuid, formData, {
          includeMetrics: true,
        });

    if (response.status === 401) {
      Handler401(navigate);
      return;
    }
    if (!response.ok) throw new Error("Upload failed");

    return await response.json();
  };

  const handleDocumentDeleted = async (summaryMetrics) => {
    await fetchDocuments();
    if (summaryMetrics) {
      setFiling((prev) => ({ ...prev, summary_metrics: summaryMetrics }));
    } else {
      await fetchData();
    }
  };

  const handleMarkCryptoComplete = async () => {
    if (!filingUuid) return;
    try {
      const response = await partialUpdateFiling(
        "tax/filing",
        { crypto_upload_completed: true },
        filingUuid,
      );
      if (response.ok) await fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  return {
    handleUploadDocument,
    handleDocumentDeleted,
    handleMarkCryptoComplete,
  };
}
